import React, { useState, useEffect } from 'react';
import { Search, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CurrencyManagement = ({ currencies, onCurrencyChange, defaultCurrency, onDefaultChange }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [availableCurrencies] = useState([
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
    // Add more currencies as needed
  ]);

  // ✅ DEFENSIVE PROGRAMMING: Ensure currencies is always an array
  const safeCurrencies = currencies || [];

  const filteredCurrencies = availableCurrencies.filter(currency =>
    currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isEnabled = (currencyCode) => {
    // ✅ FIXED: Safe check for currencies array
    return safeCurrencies.some(c => c.code === currencyCode && c.enabled);
  };

  // ✅ FIXED: Safe filtering for enabled currencies
  const enabledCurrencies = safeCurrencies.filter(c => c.enabled);

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">{t('currency.defaultCurrency')}</label>
        <select
          className="mt-1 block w-full h-[32px] rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          value={defaultCurrency}
          onChange={(e) => onDefaultChange(e.target.value)}
        >
          {/* ✅ FIXED: Safe mapping with enabledCurrencies */}
          {enabledCurrencies.map(currency => (
            <option key={currency.code} value={currency.code}>
              {currency.name} ({currency.symbol})
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('currency.availableCurrencies')}</label>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('currency.searchCurrencies')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="bg-white border rounded-md shadow-sm max-h-96 overflow-y-auto">
          {filteredCurrencies.map((currency) => {
            const enabled = isEnabled(currency.code);
            return (
              <div
                key={currency.code}
                className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => onCurrencyChange(currency)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 border rounded flex items-center justify-center">
                    {enabled && <Check className="h-3 w-3 text-blue-600" />}
                  </div>
                  <div>
                    <span className="font-medium">{currency.code}</span>
                    <span className="text-gray-500 ml-2">{currency.name}</span>
                  </div>
                </div>
                <span className="text-gray-400">{currency.symbol}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CurrencyManagement;